import styled from "@emotion/styled";
import Link from "next/link";
import { Container } from "react-bootstrap";
import Head from "next/head";
import { MessageDescriptor, PrimitiveType, useIntl } from "react-intl";
import useViewPageTracking from "@/hooks/useViewPageTracking";
import GitHubRepoAnalysisForm from "../content/GitHubRepoAnalysisForm";
import SiteLogo from "./SiteLogo";
import SiteTitle from "./SiteTitle";

const StyledHeader = styled.header`
  background-color: var(--color-background);
  padding: 1rem 0;

  display: sticky;

  a {
    display: block;
  }

  button {
    word-break: keep-all;
    white-space: nowrap;
  }

  @media (min-width: 768px) {
    button {
      display: inline-block;
    }
  }
`;

const Logo = styled.div`
  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
  }

  svg {
    width: auto;

    &:first-of-type {
      height: 3.33rem;
    }

    &:last-of-type {
      height: 1rem;
    }

    @media (min-width: 768px) {
      &:first-of-type {
        height: 5rem;
      }

      &:last-of-type {
        height: 3rem;
      }
    }
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  gap: 1rem;
  flex-grow: 0;
`;

const Search = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  gap: 1rem;
  flex-grow: 1;
`;

const Header = ({
  pageTitle,
  pageTitleParameters,
  description,
  initialSearchQuery,
}: {
  pageTitle: MessageDescriptor;
  pageTitleParameters?: Record<string, PrimitiveType>;
  description?: MessageDescriptor;
  initialSearchQuery?: string;
}) => {
  const intl = useIntl();
  useViewPageTracking();

  return (
    <>
      <Head>
        <title>{`HackStalker - ${intl.formatMessage(pageTitle, pageTitleParameters)}`}</title>

        {description && <meta name="description" content={intl.formatMessage(description)} />}
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <link rel="icon" type="image/png" href="/favicon/favicon-96x96.png" sizes="96x96" />
        <link rel="icon" type="image/svg+xml" href="/favicon/favicon.svg" />
        <link rel="shortcut icon" href="/favicon/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-title" content="HackStalker" />
        <link rel="manifest" href="/favicon/site.webmanifest" />
      </Head>
      <StyledHeader>
        <Container>
          <HeaderWrapper>
            <HeaderLeft>
              <Link href="/">
                <Logo>
                  <SiteLogo />
                  <SiteTitle />
                </Logo>
              </Link>
            </HeaderLeft>
            <Search>
              <GitHubRepoAnalysisForm initialGitHubUrl={initialSearchQuery} testId="github-url" />
            </Search>
          </HeaderWrapper>
        </Container>
      </StyledHeader>
    </>
  );
};

export default Header;
